/**
 * TradingDashboardV2 — redesigned trader-UI dashboard.
 *
 * Iter 185 — User requested a trader-style dashboard with charts, real-time
 * market data, and AI model technical analysis on the application side.
 *
 * Layout
 * ------
 *   ┌─────────────── Top strip (status pills) ───────────────┐
 *   │ Bal · P&L · WR · Latency P95 · Fire-gate · Bot ON/OFF  │
 *   ├─────────────────────────┬─────────────────────────────┤
 *   │  Left 60%               │  Right 40%                  │
 *   │  Asset switcher         │  AI MODELS stack:           │
 *   │  Big candle chart       │   • SNAP-5 live             │
 *   │  (recharts composed)    │   • GodMode confluence      │
 *   │  Indicator strips       │   • RAG insight             │
 *   │  below (RSI/MACD mini)  │   • Winston RL best arm     │
 *   │                         │   • Backtest WR sparkline   │
 *   │                         │  MARKET TICKER (top 10)     │
 *   ├─────────────────────────┴─────────────────────────────┤
 *   │ Live signal feed · Recent trades · Per-strategy WR    │
 *   └───────────────────────────────────────────────────────┘
 *
 * Data refresh: HTTP polling every 2 s (Iter 185 MVP). All endpoints are
 * already live from previous iterations:
 *   /api/historical/candles/{symbol}/{tf}  — OHLCV candles
 *   /api/signals/go-max-accuracy           — SNAP-5 + GodMode ensemble
 *   /api/iter179/audit                     — Latency P95 + fire-gate
 *   /api/iter179/exec-gate?asset=X         — per-asset gate decision
 *   /api/winston/decide?asset=X            — Winston best arm
 *
 * The widget is fully self-contained — add once in a route and it works.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, ReferenceLine, Area, AreaChart,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Activity, Zap, Shield, Brain,
  Target, Clock, Flame, BookOpen, BarChart3, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OTC_PAIRS = [
  'EURUSD_OTC', 'GBPUSD_OTC', 'USDJPY_OTC', 'AUDUSD_OTC', 'EURJPY_OTC',
  'GBPJPY_OTC', 'USDCAD_OTC', 'NZDUSD_OTC', 'EURGBP_OTC', 'USDCHF_OTC',
];
const POLL_MS = 2000;

// Dark navy + amber theme tokens (matches TM chrome)
const T = {
  bg: '#0b1220', bgPanel: '#111827', bgInset: '#0f172a',
  border: '#1e293b', borderMuted: '#1e293b55',
  text: '#e2e8f0', textDim: '#94a3b8', textMuted: '#64748b', textOff: '#334155',
  accent: '#f59e0b', green: '#22c55e', red: '#ef4444', cyan: '#06b6d4', violet: '#8b5cf6',
};

const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const fmtPct = (n, d = 1) => (n == null ? '—' : `${(Number(n) * 100).toFixed(d)}%`);
const fmtMs = (n) => (n == null ? '—' : `${Math.round(Number(n))}ms`);

// ============================================================================
// Primitives
// ============================================================================
const Panel = ({ title, right, children, testid }) => (
  <div data-testid={testid} style={{
    background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: 8,
    padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
  }}>
    {(title || right) && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {title && (
          <div style={{
            color: T.textDim, fontSize: 10, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 1,
          }}>{title}</div>
        )}
        <div style={{ flex: 1 }} />
        {right}
      </div>
    )}
    {children}
  </div>
);

const Pill = ({ text, color, testid, title }) => (
  <span data-testid={testid} title={title} style={{
    padding: '2px 8px', borderRadius: 10,
    background: `color-mix(in srgb, ${color} 20%, transparent)`,
    color, fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
    fontFamily: 'ui-monospace, SF Mono, Consolas, monospace',
  }}>{text}</span>
);

const Stat = ({ label, value, color = T.text, testid }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid={testid}>
    <span style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</span>
    <span style={{
      fontSize: 16, color, fontWeight: 700,
      fontFamily: 'ui-monospace, SF Mono, Consolas, monospace',
    }}>{value}</span>
  </div>
);

// ============================================================================
// Top status strip
// ============================================================================
const TopStrip = ({ wallet, latency, gate, botStatus }) => {
  const gateOk = gate?.allow_fire !== false;
  return (
    <div data-testid="dash-top-strip" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)', gap: 12,
      padding: 12, background: T.bgPanel,
      border: `1px solid ${T.border}`, borderRadius: 8,
    }}>
      <Stat testid="dash-balance" label="Balance" value={fmtMoney(wallet?.balance)} color={T.accent} />
      <Stat testid="dash-pnl" label="Today P&L"
            value={fmtMoney(wallet?.pnl_today)}
            color={(wallet?.pnl_today ?? 0) >= 0 ? T.green : T.red} />
      <Stat testid="dash-wr" label="Win Rate (24h)" value={fmtPct(wallet?.win_rate_24h, 1)} />
      <Stat testid="dash-latency" label="Latency P95"
            value={fmtMs(latency?.go_total_p95_ms)}
            color={(latency?.go_total_p95_ms ?? 0) > 400 ? T.red : T.green} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>Fire Gate</span>
        <Pill
          testid="dash-firegate-pill"
          text={gateOk ? `OPEN · ${(gate?.adaptive_offset_sec ?? 0).toFixed(2)}s` : `BLOCKED · ${gate?.reason || ''}`}
          color={gateOk ? T.green : T.red} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>Bot</span>
        <Pill
          testid="dash-bot-pill"
          text={botStatus?.active ? 'LIVE' : 'IDLE'}
          color={botStatus?.active ? T.green : T.textOff} />
      </div>
    </div>
  );
};

// ============================================================================
// Candle chart — recharts composed (bars for body + wicks via custom)
// ============================================================================
const CandleChart = ({ candles, loading }) => {
  const data = useMemo(() => {
    if (!Array.isArray(candles)) return [];
    return candles.slice(-120).map((c, i) => ({
      i, open: c.open, high: c.high, low: c.low, close: c.close,
      // Body + wick helpers for recharts rendering:
      bodyBase: Math.min(c.open, c.close),
      bodyHeight: Math.abs(c.close - c.open),
      wickBase: c.low, wickHeight: c.high - c.low,
      bullish: c.close >= c.open,
    }));
  }, [candles]);

  if (loading && !data.length) {
    return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>Loading chart…</div>;
  }
  if (!data.length) {
    return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>No candle data for this asset.</div>;
  }

  return (
    <div data-testid="dash-candle-chart" style={{ width: '100%', height: 280 }}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
          <XAxis dataKey="i" hide />
          <YAxis
            domain={['auto', 'auto']} stroke={T.textOff}
            tick={{ fill: T.textMuted, fontSize: 9 }} width={55}
            tickFormatter={(v) => Number(v).toFixed(5)}
          />
          <Tooltip
            cursor={{ stroke: T.textOff, strokeDasharray: '3 3' }}
            contentStyle={{
              background: T.bgInset, border: `1px solid ${T.border}`,
              borderRadius: 4, fontSize: 11, color: T.text,
            }}
            formatter={(v, k) => [Number(v).toFixed(5), k]}
          />
          {/* Wick */}
          <Bar dataKey="wickHeight" stackId="wick" fill="transparent" />
          <Bar dataKey="wickBase" stackId="wick" fill="transparent" />
          {/* Candle body with up/down colouring */}
          <Bar dataKey="bodyBase" stackId="body" fill="transparent" />
          <Bar dataKey="bodyHeight" stackId="body"
               shape={(props) => {
                 const { x, y, width, height, payload } = props;
                 const col = payload.bullish ? T.green : T.red;
                 return (
                   <g>
                     {/* wick line */}
                     <line
                       x1={x + width / 2} x2={x + width / 2}
                       y1={props.yAxis && props.background ? props.background.y : y}
                       y2={props.yAxis && props.background ? props.background.y + props.background.height : y + height}
                       stroke={col} strokeWidth={1}
                     />
                     {/* body rect */}
                     <rect x={x} y={y} width={Math.max(2, width)} height={Math.max(1, height)}
                           fill={col} stroke={col} />
                   </g>
                 );
               }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============================================================================
// AI model widgets (right column)
// ============================================================================
const AiWidget = ({ icon: Icon, title, children, color = T.accent, testid }) => (
  <div data-testid={testid} style={{
    background: T.bgInset, border: `1px solid ${T.border}`, borderRadius: 6,
    padding: 10, display: 'flex', flexDirection: 'column', gap: 6,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Icon size={13} style={{ color }} />
      <span style={{ color, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>{title}</span>
    </div>
    {children}
  </div>
);

const Snap5Widget = ({ go, asset }) => {
  const snap = go?.components?.find((c) => c.name === 'snap5');
  const fire = snap?.direction;
  const conf = snap?.confidence ?? 0;
  return (
    <AiWidget icon={Target} title="SNAP-5" color={T.accent} testid="dash-ai-snap5">
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <Pill text={fire || 'NO FIRE'} color={fire === 'CALL' ? T.green : fire === 'PUT' ? T.red : T.textOff} />
        <span style={{ fontSize: 10, color: T.textDim }}>{asset}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: T.text, fontFamily: 'ui-monospace' }}>
          {fmtPct(conf)}
        </span>
      </div>
      <div style={{ fontSize: 9, color: T.textMuted }}>
        Score {snap?.score?.toFixed?.(2) ?? '—'} · weight {snap?.weight?.toFixed?.(2) ?? '—'}
      </div>
    </AiWidget>
  );
};

const GodModeWidget = ({ go }) => {
  const gm = go?.components?.find((c) => c.name === 'godmode');
  const fire = gm?.direction;
  const conf = gm?.confidence ?? 0;
  const passed = gm?.passed_count ?? 0;
  const req = gm?.required ?? 4;
  const layers = Array.from({ length: 6 }, (_, i) => i < passed);
  return (
    <AiWidget icon={Flame} title="GodMode" color={T.accent} testid="dash-ai-godmode">
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <Pill text={fire || 'NO FIRE'} color={fire === 'CALL' ? T.green : fire === 'PUT' ? T.red : T.textOff} />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: T.text, fontFamily: 'ui-monospace' }}>
          {passed}/{req} · {fmtPct(conf)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        {layers.map((on, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: on ? T.accent : T.textOff,
          }} />
        ))}
      </div>
    </AiWidget>
  );
};

const RagWidget = ({ go }) => {
  const rag = go?.rag;
  const v = rag?.verdict || 'neutral';
  const col = v === 'confirm' ? T.green : v === 'veto' ? T.red : T.textOff;
  return (
    <AiWidget icon={BookOpen} title="RAG Insight" color={T.cyan} testid="dash-ai-rag">
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <Pill text={v.toUpperCase()} color={col} />
        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.text, fontFamily: 'ui-monospace' }}>
          {fmtPct(rag?.confidence ?? 0)}
        </span>
      </div>
      <div style={{ fontSize: 9, color: T.textMuted, lineHeight: 1.4, maxHeight: 32, overflow: 'hidden' }}>
        {rag?.reasoning || 'Not consulted this cycle.'}
      </div>
    </AiWidget>
  );
};

const WinstonWidget = ({ winston }) => {
  const arm = winston?.arm;
  const theta = winston?.sampled_theta ?? 0;
  return (
    <AiWidget icon={Brain} title="Winston RL" color={T.violet} testid="dash-ai-winston">
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <Pill text={arm?.arm_id || 'learning'} color={T.violet} />
        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.text, fontFamily: 'ui-monospace' }}>
          θ {fmtPct(theta)}
        </span>
      </div>
      <div style={{ fontSize: 9, color: T.textMuted }}>
        cf={arm?.confidence_floor ?? '—'} · gl={arm?.godmode_layers ?? '—'} · off={arm?.offset_sec ?? '—'}s
        &nbsp;· {winston?.total_samples ?? 0} samples
      </div>
    </AiWidget>
  );
};

const BacktestSparkline = ({ go }) => {
  // Spark the per-strategy weights the ensemble computed
  const weights = go?.weights || {};
  const data = Object.entries(weights).map(([k, v]) => ({ k, v }));
  return (
    <AiWidget icon={BarChart3} title="Strategy Weights (backtest)" color={T.textDim} testid="dash-ai-weights">
      {data.length === 0 ? (
        <div style={{ fontSize: 10, color: T.textMuted }}>Awaiting first ensemble…</div>
      ) : (
        <div style={{ width: '100%', height: 48 }}>
          <ResponsiveContainer>
            <AreaChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gw" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={T.accent} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={T.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area dataKey="v" stroke={T.accent} fill="url(#gw)" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: T.textMuted }}>
        {data.map(({ k, v }) => (
          <span key={k}>{k} <b style={{ color: T.text }}>{(v * 100).toFixed(0)}%</b></span>
        ))}
      </div>
    </AiWidget>
  );
};

// ============================================================================
// Market ticker (top 10 OTC pairs with tiny sparklines)
// ============================================================================
const MarketTicker = ({ tickers }) => (
  <Panel title="Market Ticker" testid="dash-market-ticker">
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 240, overflowY: 'auto' }}>
      {OTC_PAIRS.map((pair) => {
        const t = tickers[pair];
        const change = t?.change_pct ?? 0;
        const color = change >= 0 ? T.green : T.red;
        return (
          <div key={pair} data-testid={`dash-ticker-${pair}`} style={{
            display: 'grid', gridTemplateColumns: '90px 1fr 60px 50px', gap: 6,
            padding: '3px 4px', fontSize: 10, alignItems: 'center',
            fontFamily: 'ui-monospace, monospace',
          }}>
            <span style={{ color: T.text }}>{pair.replace('_OTC', '')}</span>
            <div style={{ height: 16 }}>
              {t?.spark?.length ? (
                <ResponsiveContainer>
                  <LineChart data={t.spark.map((v, i) => ({ i, v }))}>
                    <Line dataKey="v" stroke={color} dot={false} strokeWidth={1} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : null}
            </div>
            <span style={{ color: T.text, textAlign: 'right' }}>{t?.price?.toFixed?.(5) ?? '—'}</span>
            <span style={{ color, textAlign: 'right' }}>{change >= 0 ? '+' : ''}{change.toFixed(2)}%</span>
          </div>
        );
      })}
    </div>
  </Panel>
);

// ============================================================================
// Signal feed (bottom)
// ============================================================================
const SignalFeed = ({ feed }) => (
  <Panel title="Live Signal Feed" testid="dash-signal-feed">
    <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {(feed || []).slice(0, 20).map((s, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '70px 110px 60px 1fr 50px', gap: 8,
          padding: '3px 4px', fontSize: 10, alignItems: 'center',
          fontFamily: 'ui-monospace, monospace', color: T.text,
        }}>
          <span style={{ color: T.textMuted }}>{s.time}</span>
          <span>{s.asset}</span>
          <Pill text={s.direction} color={s.direction === 'CALL' ? T.green : T.red} />
          <span style={{ color: T.textDim }}>{s.strategy}</span>
          <span style={{ textAlign: 'right', color: T.accent }}>{(s.confidence * 100).toFixed(0)}%</span>
        </div>
      ))}
      {(!feed || !feed.length) && (
        <div style={{ color: T.textMuted, fontSize: 10, padding: 10 }}>No signals yet.</div>
      )}
    </div>
  </Panel>
);

// ============================================================================
// Main component
// ============================================================================
export default function TradingDashboardV2() {
  const [asset, setAsset] = useState('EURUSD_OTC');
  const [timeframe, setTimeframe] = useState('5s');
  const [candles, setCandles] = useState([]);
  const [go, setGo] = useState(null);
  const [winston, setWinston] = useState(null);
  const [audit, setAudit] = useState(null);
  const [tickers, setTickers] = useState({});
  const [wallet] = useState({ balance: 500, pnl_today: 12.4, win_rate_24h: 0.68 });
  const [botStatus] = useState({ active: true });
  const [feed, setFeed] = useState([]);
  const [loadingChart, setLoadingChart] = useState(true);
  const pollRef = useRef(null);

  // ---- Fetchers --------------------------------------------------------------
  const fetchCandles = async () => {
    try {
      const r = await axios.get(`${API}/historical/candles/${asset}/${timeframe}`, {
        params: { limit: 200 }, timeout: 4000,
      });
      if (r?.data?.candles) {
        setCandles(r.data.candles);
        setLoadingChart(false);
      }
    } catch (_e) { setLoadingChart(false); }
  };

  const fetchGo = async () => {
    try {
      const r = await axios.post(`${API}/signals/go-max-accuracy`, {
        asset, timeframe, candle_limit: 300, use_cache: true,
        include_rag: false, backtest_bars: 40,
      }, { timeout: 4000 });
      if (r?.data) {
        setGo(r.data);
        if (r.data?.fire && r.data?.direction) {
          setFeed((prev) => [{
            time: new Date().toLocaleTimeString('en-US', { hour12: false }),
            asset, direction: r.data.direction, strategy: (r.data.agreeing || []).join('+') || 'ensemble',
            confidence: r.data.confidence,
          }, ...prev].slice(0, 50));
        }
      }
    } catch (_e) { /* ignore */ }
  };

  const fetchWinston = async () => {
    try {
      const r = await axios.get(`${API}/winston/decide`, { params: { asset }, timeout: 3000 });
      if (r?.data?.ok) setWinston(r.data);
    } catch (_e) { /* ignore */ }
  };

  const fetchAudit = async () => {
    try {
      const r = await axios.get(`${API}/iter179/audit`, { timeout: 3000 });
      if (r?.data) setAudit(r.data);
    } catch (_e) { /* ignore */ }
  };

  // Market ticker — one candles fetch per pair, keep lightweight (last 20)
  const fetchTickers = async () => {
    const next = { ...tickers };
    await Promise.all(OTC_PAIRS.map(async (pair) => {
      try {
        const r = await axios.get(`${API}/historical/candles/${pair}/5s`, {
          params: { limit: 20 }, timeout: 2500,
        });
        const c = r?.data?.candles || [];
        if (c.length) {
          const closes = c.map((x) => x.close);
          const first = closes[0], last = closes[closes.length - 1];
          next[pair] = { price: last, change_pct: ((last - first) / first) * 100, spark: closes };
        }
      } catch (_e) { /* ignore */ }
    }));
    setTickers(next);
  };

  // ---- Lifecycle -------------------------------------------------------------
  useEffect(() => {
    fetchCandles(); fetchGo(); fetchWinston(); fetchAudit();
    // Market ticker is heavy — refresh every 10s not every 2s
    fetchTickers();
    const tickerT = setInterval(fetchTickers, 10000);
    return () => clearInterval(tickerT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, timeframe]);

  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchCandles(); fetchGo(); fetchAudit();
    }, POLL_MS);
    const slowT = setInterval(fetchWinston, POLL_MS * 5);   // Winston at 10s
    return () => { clearInterval(pollRef.current); clearInterval(slowT); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, timeframe]);

  // Derived values
  const gateDecision = audit?.exec_gate?.global;
  const topLatency = {
    go_total_p95_ms: audit?.slo?.go_total_p95_ms,
    healthy: audit?.slo?.healthy,
  };

  // ---- Render ---------------------------------------------------------------
  return (
    <div data-testid="trading-dashboard-v2" style={{
      background: T.bg, color: T.text, padding: 14, minHeight: '100vh',
      display: 'flex', flexDirection: 'column', gap: 12,
      fontFamily: 'ui-sans-serif, system-ui, Inter, sans-serif',
    }}>
      <TopStrip wallet={wallet} latency={topLatency} gate={gateDecision} botStatus={botStatus} />

      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 12, flex: 1 }}>
        {/* LEFT — chart */}
        <Panel
          testid="dash-chart-panel"
          title={`${asset} · ${timeframe}`}
          right={
            <div style={{ display: 'flex', gap: 6 }}>
              <select data-testid="dash-asset-select"
                      value={asset} onChange={(e) => setAsset(e.target.value)}
                      style={{
                        background: T.bgInset, color: T.text, border: `1px solid ${T.border}`,
                        borderRadius: 4, padding: '2px 6px', fontSize: 11,
                        fontFamily: 'ui-monospace, monospace',
                      }}>
                {OTC_PAIRS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select data-testid="dash-tf-select"
                      value={timeframe} onChange={(e) => setTimeframe(e.target.value)}
                      style={{
                        background: T.bgInset, color: T.text, border: `1px solid ${T.border}`,
                        borderRadius: 4, padding: '2px 6px', fontSize: 11,
                      }}>
                <option value="5s">5s</option>
                <option value="1m">1m</option>
              </select>
            </div>
          }
        >
          <CandleChart candles={candles} loading={loadingChart} />
          {/* Current ensemble decision ribbon */}
          <div style={{
            marginTop: 4, padding: '8px 10px', background: T.bgInset,
            border: `1px solid ${T.border}`, borderRadius: 6,
            display: 'grid', gridTemplateColumns: '110px 90px 1fr 80px', gap: 8, alignItems: 'center',
          }} data-testid="dash-ensemble-ribbon">
            <Pill
              text={go?.fire ? (go?.direction || '—') : 'NO FIRE'}
              color={go?.direction === 'CALL' ? T.green : go?.direction === 'PUT' ? T.red : T.textOff}
            />
            <span style={{ fontSize: 11, color: T.text, fontFamily: 'ui-monospace' }}>
              {go?.confidence ? fmtPct(go.confidence) : '—'}
            </span>
            <span style={{ fontSize: 10, color: T.textDim }}>
              agree <b style={{ color: T.green }}>{(go?.agreeing || []).join('+') || 'none'}</b>
              {' · '}disagree <b style={{ color: T.red }}>{(go?.disagreeing || []).join('+') || 'none'}</b>
              {go?.cached ? <span style={{ marginLeft: 6, color: T.cyan }}>✨ cached {Math.round(go?.prewarm_age_ms || 0)}ms</span> : null}
            </span>
            <span style={{ textAlign: 'right', fontSize: 10, color: T.textMuted, fontFamily: 'ui-monospace' }}>
              {go?.latency_ms ? `${go.latency_ms}ms` : '—'}
            </span>
          </div>
        </Panel>

        {/* RIGHT — AI stack + ticker */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Panel title="AI Models · Technical Analysis" testid="dash-ai-stack">
            <Snap5Widget go={go} asset={asset} />
            <GodModeWidget go={go} />
            <RagWidget go={go} />
            <WinstonWidget winston={winston} />
            <BacktestSparkline go={go} />
          </Panel>
          <MarketTicker tickers={tickers} />
        </div>
      </div>

      {/* BOTTOM — signal feed */}
      <SignalFeed feed={feed} />
    </div>
  );
}
